import { useEffect, useMemo, useState } from 'react';
import type { GameType } from '@hypevault/shared-types';
import { Watermark } from './Watermark';
import { TriviaBody } from './games/TriviaBody';
import { HangmanBody } from './games/HangmanBody';
import { ChatWordsBody } from './games/ChatWordsBody';
import { FeudBody } from './games/FeudBody';
import { CrosswordBody } from './games/CrosswordBody';
import { BattleshipBody } from './games/BattleshipBody';

/** The unified `game` browser source only renders the 6 standard
 *  games in this widget. HypeFish and HypeEgg have their own browser
 *  sources (HypefishWidget / HypeEggWidget); their ticks publish to
 *  the same `streamer:<id>:games` channel so without this filter the
 *  games-overlay snapshot would flip to hypeegg/hypefish state every
 *  time their engine ticked. */
const STANDARD_GAMES: ReadonlySet<string> = new Set([
  'trivia',
  'hangman',
  'chatwords',
  'feud',
  'crossword',
  'battleship',
]);

interface GameStateEvent {
  gameType: string;
  state: unknown;
  config: unknown;
  isActive: boolean;
}

interface GameSnapshot {
  gameType: string | null;
  isActive: boolean;
  state: unknown;
  config: unknown;
}

interface GameMeta {
  label: string;
  accent: string;
  glow: string;
  emoji: string;
  tagline: string;
  eyebrow: string;
}

/** Accent colors mapped 1:1 with the redesign spec. HypeEgg + HypeFish
 *  are intentionally absent — they own their own browser sources and
 *  must not render through the unified games overlay. */
const GAME_META: Partial<Record<GameType, GameMeta>> = {
  trivia: {
    label: 'TRIVIA',
    accent: '#f59e0b',
    glow: 'rgba(245,158,11,0.55)',
    emoji: '🧠',
    tagline: 'Type !a A · B · C · D',
    eyebrow: 'Question Round',
  },
  hangman: {
    label: 'HANGMAN',
    accent: '#8b5cf6',
    glow: 'rgba(139,92,246,0.55)',
    emoji: '🪢',
    tagline: 'Type !guess <letter>',
    eyebrow: 'Save the figure',
  },
  chatwords: {
    label: 'CHATWORDS',
    accent: '#10b981',
    glow: 'rgba(16,185,129,0.55)',
    emoji: '🔠',
    tagline: 'Type !cw <word>',
    eyebrow: 'Find the words',
  },
  feud: {
    label: 'FEUD',
    accent: '#f97316',
    glow: 'rgba(249,115,22,0.55)',
    emoji: '👥',
    tagline: 'Type !feud <answer>',
    eyebrow: 'Survey says…',
  },
  crossword: {
    label: 'CROSSWORD',
    accent: '#06b6d4',
    glow: 'rgba(6,182,212,0.55)',
    emoji: '🧩',
    tagline: 'Type !cw-solve <#> <answer>',
    eyebrow: 'Across & Down',
  },
  battleship: {
    label: 'CHATTLE SHIP',
    accent: '#3b82f6',
    glow: 'rgba(59,130,246,0.55)',
    emoji: '🚢',
    tagline: 'Type !fire <coord>',
    eyebrow: 'Hunt the fleet',
  },
};

export function GameWidget({
  token,
  gameStateEvent,
  streamerTier = 'free',
}: {
  token: string;
  gameStateEvent: GameStateEvent | null;
  streamerTier?: 'free' | 'paid';
}) {
  const [snapshot, setSnapshot] = useState<GameSnapshot>({
    gameType: null,
    isActive: false,
    state: null,
    config: null,
  });

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`/api/games/active?token=${encodeURIComponent(token)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        if (cancelled || !json) return;
        // Ignore non-standard games even if they happen to be the
        // active row — HypeEgg / HypeFish own their own browser
        // sources and must not surface here.
        if (json.gameType && STANDARD_GAMES.has(json.gameType)) {
          setSnapshot({
            gameType: json.gameType,
            isActive: !!json.isActive,
            state: json.state ?? null,
            config: json.config ?? null,
          });
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!gameStateEvent) return;
    // Drop pubs from widget-independent games — they share the
    // `streamer:<id>:games` channel but render in their own widgets.
    // Without this guard, a HypeEgg tick would flip this snapshot to
    // hypeegg mid-trivia round.
    if (!STANDARD_GAMES.has(gameStateEvent.gameType)) return;
    setSnapshot({
      gameType: gameStateEvent.gameType,
      isActive: gameStateEvent.isActive,
      state: gameStateEvent.state,
      config: gameStateEvent.config,
    });
  }, [gameStateEvent]);

  const meta = useMemo<GameMeta | null>(() => {
    if (!snapshot.gameType) return null;
    return GAME_META[snapshot.gameType as GameType] ?? null;
  }, [snapshot.gameType]);

  if (!snapshot.gameType || !snapshot.isActive || !meta) {
    return <IdleStage streamerTier={streamerTier} />;
  }

  const cssVars: React.CSSProperties = {
    ['--gx-color' as string]: meta.accent,
    ['--gx-glow' as string]: meta.glow,
  };

  const hud = computeHud(snapshot, meta);

  return (
    <div className="gx-stage" style={cssVars}>
      <Hud meta={meta} hud={hud} />
      <div className="gx-body-region">
        <GameBody
          gameType={snapshot.gameType}
          state={snapshot.state}
          config={snapshot.config}
          meta={meta}
        />
      </div>
      {streamerTier === 'free' && (
        <div
          style={{
            position: 'absolute',
            right: 24,
            bottom: 18,
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <Watermark variant="inline" />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// HUD — top bar shared across every game
// ─────────────────────────────────────────────────────────────────────

interface HudData {
  roundLabel: string | null;
  roundValue: string | null;
  timerSeconds: number | null;
  topScores: Array<{ name: string; score: number }>;
}

function Hud({ meta, hud }: { meta: GameMeta; hud: HudData }) {
  return (
    <div className="gx-hud">
      <div className="gx-hud-left">
        <span className="gx-hud-emoji" aria-hidden>
          {meta.emoji}
        </span>
        <div className="gx-hud-title-block">
          <div className="gx-hud-eyebrow">{meta.eyebrow}</div>
          <div className="gx-hud-title">{meta.label}</div>
        </div>
        <span className="gx-hud-live">Live</span>
      </div>

      <div className="gx-hud-center">
        {hud.roundLabel && hud.roundValue && (
          <div className="gx-hud-round">
            <span className="gx-hud-round-label">{hud.roundLabel}</span>
            <span className="gx-hud-round-num">{hud.roundValue}</span>
          </div>
        )}
        {hud.timerSeconds != null && <HudTimer seconds={hud.timerSeconds} />}
      </div>

      <div className="gx-hud-right">
        <div className="gx-hud-scores">
          {hud.topScores.slice(0, 3).map((s, i) => (
            <div key={s.name} className={`gx-hud-score ${i === 0 ? 'top' : ''}`}>
              <span className="gx-hud-score-rank">#{i + 1}</span>
              <span className="gx-hud-score-name">{s.name}</span>
              <span className="gx-hud-score-pts">{s.score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Live-tick timer using a single setInterval. Re-derives remaining seconds
 * from the absolute target each tick so it self-corrects on tab-throttle.
 */
function HudTimer({ seconds }: { seconds: number }) {
  const [now, setNow] = useState(() => Date.now());
  const targetRef = useMemo(() => Date.now() + seconds * 1000, [seconds]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  const remain = Math.max(0, (targetRef - now) / 1000);
  const warn = remain <= 10;
  const m = Math.floor(remain / 60);
  const s = Math.floor(remain % 60)
    .toString()
    .padStart(2, '0');
  const display = m > 0 ? `${m}:${s}` : `${Math.ceil(remain)}s`;
  return (
    <div className={`gx-hud-timer ${warn ? 'warn' : ''}`}>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        style={{ color: warn ? '#fca5a5' : 'white' }}
      >
        <circle cx="12" cy="13" r="8" />
        <path d="M12 9v4l2 2M9 2h6" />
      </svg>
      <span className="gx-hud-timer-num">{display}</span>
    </div>
  );
}

function computeHud(snapshot: GameSnapshot, _meta: GameMeta): HudData {
  const s = snapshot.state as Record<string, unknown> | null;
  const c = snapshot.config as Record<string, unknown> | null;
  if (!s) {
    return { roundLabel: null, roundValue: null, timerSeconds: null, topScores: [] };
  }

  // Round
  let roundLabel: string | null = null;
  let roundValue: string | null = null;
  if (snapshot.gameType === 'trivia') {
    const total = (c?.maxQuestions as number | undefined) ?? null;
    const idx = (s.index as number | undefined) ?? 0;
    roundLabel = 'Question';
    roundValue = total ? `${idx + 1}/${total}` : String(idx + 1);
  } else if (snapshot.gameType === 'hangman') {
    const wrong = (s.wrongGuesses as number | undefined) ?? 0;
    const max = (c?.maxWrong as number | undefined) ?? 6;
    roundLabel = 'Lives';
    roundValue = `${max - wrong}/${max}`;
  } else if (snapshot.gameType === 'chatwords') {
    const lvl = (s.level as number | undefined) ?? 1;
    roundLabel = 'Level';
    roundValue = String(lvl);
  } else if (snapshot.gameType === 'crossword') {
    const solved = Array.isArray(s.solved) ? (s.solved as unknown[]).length : 0;
    const clues = Array.isArray(c?.clues) ? (c!.clues as unknown[]).length : null;
    roundLabel = 'Solved';
    roundValue = clues ? `${solved}/${clues}` : String(solved);
  } else if (snapshot.gameType === 'feud') {
    const survey = s.currentSurvey as { answers?: unknown[] } | null | undefined;
    const total = survey?.answers?.length ?? 0;
    const revealed = Array.isArray(s.revealed) ? (s.revealed as unknown[]).length : 0;
    roundLabel = 'Found';
    roundValue = total > 0 ? `${revealed}/${total}` : String(revealed);
  } else if (snapshot.gameType === 'battleship') {
    const ships = Array.isArray(s.ships)
      ? (s.ships as Array<{ size?: number; hits?: number }>)
      : [];
    const sunk = ships.filter((sh) => (sh.hits ?? 0) >= (sh.size ?? 0)).length;
    roundLabel = 'Sunk';
    roundValue = `${sunk}/${ships.length}`;
  }

  // Timer — first present field wins.
  let timerSeconds: number | null = null;
  const endsAt = (s.endsAt as number | undefined) ?? null;
  const questionExpiresAt = (s.questionExpiresAt as number | undefined) ?? null;
  const sectionEndsAt = (s.sectionEndsAt as number | undefined) ?? null;
  const levelEndsAt = (s.levelEndsAt as number | undefined) ?? null;
  const sourceMs = endsAt ?? questionExpiresAt ?? sectionEndsAt ?? levelEndsAt;
  if (sourceMs) {
    const left = (sourceMs - Date.now()) / 1000;
    if (left > 0 && left < 3600) timerSeconds = left;
  }

  // Top scores
  const topScores: Array<{ name: string; score: number }> = [];
  const scores = s.scores as Record<string, number> | undefined;
  if (scores) {
    for (const [name, score] of Object.entries(scores)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)) {
      topScores.push({ name, score });
    }
  }

  return { roundLabel, roundValue, timerSeconds, topScores };
}

// ─────────────────────────────────────────────────────────────────────
// Idle / body dispatch
// ─────────────────────────────────────────────────────────────────────

function IdleStage({ streamerTier }: { streamerTier: 'free' | 'paid' }) {
  return (
    <div className="gx-stage">
      <div className="gx-idle-stage">
        <div className="gx-idle-panel">
          <span className="gx-idle-dot" />
          Waiting for a game…
        </div>
      </div>
      {streamerTier === 'free' && (
        <div
          style={{
            position: 'absolute',
            right: 24,
            bottom: 18,
            pointerEvents: 'none',
            zIndex: 5,
          }}
        >
          <Watermark variant="inline" />
        </div>
      )}
    </div>
  );
}

function GameBody({
  gameType,
  state,
  config,
  meta,
}: {
  gameType: string;
  state: unknown;
  config: unknown;
  meta: GameMeta;
}) {
  switch (gameType) {
    case 'trivia':
      return (
        <TriviaBody
          state={state as Parameters<typeof TriviaBody>[0]['state']}
          config={config as Parameters<typeof TriviaBody>[0]['config']}
        />
      );
    case 'hangman':
      return (
        <HangmanBody
          state={state as Parameters<typeof HangmanBody>[0]['state']}
          config={config as Parameters<typeof HangmanBody>[0]['config']}
        />
      );
    case 'chatwords':
      return (
        <ChatWordsBody
          state={state as Parameters<typeof ChatWordsBody>[0]['state']}
          config={config}
        />
      );
    case 'feud':
      return <FeudBody state={state as Parameters<typeof FeudBody>[0]['state']} config={config} />;
    case 'crossword':
      return (
        <CrosswordBody
          state={state as Parameters<typeof CrosswordBody>[0]['state']}
          config={config as Parameters<typeof CrosswordBody>[0]['config']}
        />
      );
    case 'battleship':
      return (
        <BattleshipBody
          state={state as Parameters<typeof BattleshipBody>[0]['state']}
          config={config}
        />
      );
    default:
      return (
        <div className="gx-idle-stage">
          <div className="gx-idle-panel">
            <span className="gx-idle-dot" />
            {meta.label} rebuilding…
          </div>
        </div>
      );
  }
}
